import {Label, Link, RelativeTime} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {InfoIcon} from '@primer/octicons-react'
import type {CommitSignatureType} from '../signed-commit-types'
import {GitHubAvatar} from '@github-ui/github-avatar'

import styles from './SignedCommitFooter.module.css'

export type Signer = {
  login: string
  avatar_url: string
}

export default function SignedCommitFooter({
  keyHex,
  signatureType,
  signer,
  verifiedAt,
  showPartiallyVerifiedMessage,
  signingLinkComponent,
  keyExpired,
  keyRevoked,
}: {
  /** The hex fingerprint of the SSH key or the GPG key id */
  keyHex: string
  signatureType: CommitSignatureType
  signer: Signer | undefined
  verifiedAt: string | undefined
  showPartiallyVerifiedMessage: boolean
  signingLinkComponent: React.ReactElement
  keyExpired: boolean
  keyRevoked: boolean
}) {
  const keyLabel = signatureType === 'SshSignature' ? 'SSH Key Fingerprint:' : 'GPG Key ID:'
  const keyName = signatureType === 'SshSignature' ? 'SSH' : 'GPG'
  return (
    <div data-testid="signed-commit-footer" className="d-flex">
      <div className={styles.footerContent}>
        {showPartiallyVerifiedMessage && signer ? (
          <>
            <div className="tmp-pt-3 tmp-pb-3">
              <div className="d-flex">
                <GitHubAvatar src={signer.avatar_url} size={20} className="mr-2 tmp-ml-3" />
                <div className="flex-1">
                  <Link href={`/${signer.login}`} className={styles.Link}>
                    {signer.login}
                  </Link>
                  <span>&apos;s contribution has been verified via {keyName} key.</span>
                </div>
              </div>
            </div>
            <div className="bgColor-attention-muted tmp-p-3 d-flex">
              <Octicon icon={InfoIcon} className="mr-2" />
              We cannot verify signatures from co-authors, and some of the co-authors attributed to this commit require
              their commits to be signed.
            </div>
          </>
        ) : (
          <>
            {signer && signer.login ? (
              <div data-testid="github-user-data" className="tmp-pl-3 tmp-pr-3 tmp-pt-3">
                <>
                  <GitHubAvatar src={signer.avatar_url} size={20} className="mr-2" />
                  <Link href={`/${signer.login}`} className={styles.Link_1}>
                    {signer.login}
                  </Link>
                </>
              </div>
            ) : (
              <></>
            )}
            <div className="tmp-p-3 flex-column">
              {keyHex && (
                <div>
                  {keyLabel} <span className="bgColor-muted">{keyHex}</span>
                </div>
              )}
              {verifiedAt && (
                <div>
                  Verified{' '}
                  <RelativeTime datetime={verifiedAt} threshold="PT0S" year="numeric" hour="2-digit" minute="2-digit" />
                </div>
              )}
              {keyHex && (keyExpired || keyRevoked) && (
                <div className="pt-1 pb-1">
                  {keyExpired && (
                    <Label variant="accent" className="mr-1">
                      Expired
                    </Label>
                  )}
                  {keyRevoked && <Label variant="attention">Revoked</Label>}
                </div>
              )}
              {signingLinkComponent}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
